import React, { useEffect, useState } from 'react';
import { X, Plus, Send, Loader2, Globe, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';

/**
 * Modal that lets the user pick a media and optionally provide a one-shot
 * editorial briefing for THIS article + THIS media combination.
 * Props:
 *   - open (bool)
 *   - onClose ()
 *   - onSelect (media, extraInstruction)
 */
export default function MediaPicker({ open, onClose, onSelect }) {
  const [medias, setMedias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [extraInstruction, setExtraInstruction] = useState('');
  const nav = useNavigate();

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setExtraInstruction('');
    api.get('/wordpress-sites')
      .then((r) => setMedias(r.data))
      .catch(() => setMedias([]))
      .finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="media-picker">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-[440px] max-h-[85vh] bg-white rounded-3xl shadow-2xl fade-in-up flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="font-bold text-slate-900 text-lg">Choisir un média</h3>
          <button onClick={onClose} data-testid="btn-close-picker" className="p-1.5 rounded-lg hover:bg-slate-100">
            <X size={20} />
          </button>
        </div>

        {!loading && medias.length > 0 && (
          <div className="px-5 py-3 bg-amber-50 border-b border-amber-100">
            <label className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-amber-700 mb-1.5">
              <Sparkles size={12} /> Consigne particulière (facultatif)
            </label>
            <textarea
              value={extraInstruction}
              onChange={(e) => setExtraInstruction(e.target.value)}
              placeholder="Ex: angle polémique, focus sur l'aspect économique, ton plus grand public…"
              rows={2}
              maxLength={400}
              className="w-full px-3 py-2 text-sm bg-white border border-amber-200 rounded-xl focus:ring-2 focus:ring-amber-400 outline-none resize-none"
              data-testid="extra-instruction-input"
            />
            <p className="text-[10px] text-amber-700 mt-1">
              S'applique uniquement à cet article sur le média sélectionné. {extraInstruction.length}/400
            </p>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
          {loading && (
            <div className="py-8 text-center"><Loader2 className="mx-auto animate-spin text-blue-600" /></div>
          )}
          {!loading && medias.length === 0 && (
            <div className="py-8 text-center">
              <div className="mx-auto w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mb-2">
                <Globe className="text-blue-600" />
              </div>
              <p className="text-slate-700 font-semibold">Aucun média configuré</p>
              <p className="text-sm text-slate-500 mt-1">Créez un média pour pouvoir générer.</p>
              <button
                onClick={() => { onClose(); nav('/settings/sites'); }}
                className="mt-4 bg-blue-600 text-white font-semibold px-5 py-2.5 rounded-xl"
                data-testid="btn-create-media"
              >
                <Plus size={16} className="inline mr-1 -mt-0.5" />
                Créer un média
              </button>
            </div>
          )}
          {!loading && medias.map((m) => (
            <button
              key={m.id}
              onClick={() => onSelect(m, extraInstruction.trim() || null)}
              data-testid={`pick-media-${m.id}`}
              className="w-full text-left bg-white border-2 border-slate-200 hover:border-blue-600 hover:bg-blue-50 rounded-2xl p-4 transition active:scale-[0.98]"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900">{m.name}</div>
                  <div className="text-xs text-slate-500 truncate">{m.url}</div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {m.default_author_name && (
                      <span className="text-[11px] px-2 py-0.5 rounded-md bg-slate-100 text-slate-600">
                        {m.default_author_name}
                      </span>
                    )}
                    {m.default_category_name && (
                      <span className="text-[11px] px-2 py-0.5 rounded-md bg-slate-100 text-slate-600">
                        {m.default_category_name}
                      </span>
                    )}
                    {m.paragraphs && (
                      <span className="text-[11px] px-2 py-0.5 rounded-md bg-blue-50 text-blue-700">
                        {m.paragraphs} §
                      </span>
                    )}
                    <span className="text-[11px] px-2 py-0.5 rounded-md bg-blue-50 text-blue-700">
                      {m.default_status === 'publish' ? 'Publier' : 'Brouillon'}
                    </span>
                  </div>
                  {m.prompt_template && (
                    <div className="text-xs text-slate-500 mt-2 italic line-clamp-2">
                      « {m.prompt_template} »
                    </div>
                  )}
                </div>
                <Send size={18} className="text-blue-600 flex-shrink-0 mt-1" />
              </div>
            </button>
          ))}
        </div>

        {!loading && medias.length > 0 && (
          <div className="px-4 pb-4 pt-2 border-t border-slate-100">
            <button
              onClick={() => { onClose(); nav('/settings/sites'); }}
              className="w-full border-2 border-slate-200 text-slate-600 font-semibold py-2.5 rounded-xl hover:bg-slate-50 flex items-center justify-center gap-2 text-sm"
              data-testid="btn-add-media-picker"
            >
              <Plus size={16} /> Ajouter un média
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
